'use client'

import { useCallback, useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { UNAUTHENTICATED_CART_ID_LOCAL_STORAGE_KEY } from 'modules/auth'
import { extractTokenFromClientSession } from 'modules/customer'
import { noop } from 'shared/utils/common'
import { isNotNil, isNotNilOrEmpty } from 'shared/utils/ramda'
import { LocalStorage } from 'shared/utils/storage'

import useCartStore from '../../store/cartStore'

import { MergedCartItemsInterface } from './MergeCartsModal'

const MergeCartsModal = dynamic(
  () => import('./MergeCartsModal')
)

const GuestCartInitializer = () => {
  const session = useSession()
  const { createCart, cart, getCartById, mergeCarts } = useCartStore()
  const [mergeModalOpen, setMergeModalOpen] = useState(false)
  const [mergedCartItems, setMergedCartItems] =
    useState<MergedCartItemsInterface | null>(null)

  useEffect(() => {
    if (isNotNilOrEmpty(mergedCartItems)) {
      setMergeModalOpen(true)
    }
  }, [mergedCartItems])

  useEffect(() => {
    if (session.status === 'loading') {
      return
    }

    const token = extractTokenFromClientSession(session)

    const localUnauthenticatedCartId = new LocalStorage(
      UNAUTHENTICATED_CART_ID_LOCAL_STORAGE_KEY
    )

    const localCartId = localUnauthenticatedCartId.get<string | null>(null)
    const isLoggedIn = isNotNilOrEmpty(token)
    const hasCart = isNotNil(cart)
    const isTempCartActive = localCartId === cart?.id

    const createLocalCart = async () => {
      try {
        const response = await createCart(session)

        if (response?.id) {
          localUnauthenticatedCartId.set(response.id)
        }

        return response
      } catch (error) {
        reportSentryError(error, 'Failed to create cart')

        return null
      }
    }

    if (!isLoggedIn) {
      //isUnauthenticated
      if (localCartId && !hasCart) {
        const getLocalCart = async () => {
          try {
            await getCartById(localCartId)
          } catch (err) {
            if (
              err instanceof AxiosError &&
              (err.response?.status === 404 || err.response?.status === 500)
            ) {
              localUnauthenticatedCartId.remove()

              void createLocalCart()
            } else {
              reportSentryError(err, 'Failed to get cart by id')
            }
          }
        }

        void getLocalCart()
      } else if (!hasCart) {
        //create local Cart
        void createLocalCart()
      }
    } else {
      //isLoggedIn

      if (!hasCart || isTempCartActive) {
        if (localCartId) {
          mergeCarts(String(localCartId), session)
            .then((resp: MergedCartItemsInterface | null) => {
              localUnauthenticatedCartId.remove()
              setMergedCartItems(resp)
            })
            .catch((err: unknown) => {
              if (
                err instanceof AxiosError &&
                (err.response?.status === 404 || err.response?.status === 500)
              ) {
                localUnauthenticatedCartId.remove()
                void createCart(session)
              }
              reportSentryError(err, 'Failed to merge carts')
            })
        } else {
          //create new cart(or get existing cart)
          localUnauthenticatedCartId.remove()
          void createCart(session).finally(noop)
        }
      }
    }
    // after run useEffect, we don't want to run it again except for cart?.id or session.status changes
    // (it could makes infinite loop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id, session.status])

  const onClose = useCallback(() => {
    const localUnauthenticatedCartId = new LocalStorage(
      UNAUTHENTICATED_CART_ID_LOCAL_STORAGE_KEY
    )

    localUnauthenticatedCartId.remove()
    setMergeModalOpen(false)
  }, [])

  return (
    <MergeCartsModal
      open={mergeModalOpen}
      mergedCartItems={mergedCartItems}
      onClose={onClose}
    />
  )
}

export default GuestCartInitializer
